const createWebSocketService = (initialCallback) => {

    let ws = null;
    let connectionAttempts = 0;
    const MAX_CONNECTION_ATTEMPTS = 3;
    let onMessageCallback = initialCallback;
    let isConnecting = false;
    let isModalOpen = false;
    let isBrowserSpeech = false;
    let previousModalState = null;
    let idleTimer = null;
    const IDLE_TIMEOUT = 2 * 60 * 1000;
    let lastHoldValue = null;

    const resetConnectionAttempts = () => {
        connectionAttempts = 0;
    };

    const startIdleTimer = () => {
        clearIdleTimer();
        idleTimer = setTimeout(() => {
            console.warn("WebSocket idle timeout reached, closing connection");
            closeWebSocket();
        }, IDLE_TIMEOUT);
    };

    const clearIdleTimer = () => {
        if (idleTimer) {
            clearTimeout(idleTimer);
            idleTimer = null;
        }
    };

    const updateCallback = (newCallback) => {
        if (typeof newCallback === 'function') {
            onMessageCallback = newCallback;
        }
    };

    const setupWebSocketAsync = () => {
        return new Promise((resolve, reject) => {
            if (ws && ws.readyState === WebSocket.OPEN) {
                return resolve(ws);
            }

            if (isConnecting) {
                const checkInterval = setInterval(() => {
                    if (!isConnecting && ws && ws.readyState === WebSocket.OPEN) {
                        clearInterval(checkInterval);
                        resolve(ws);
                    } else if (!isConnecting) {
                        clearInterval(checkInterval);
                        reject(new Error("WebSocket connection failed"));
                    }
                }, 100);
                return;
            }

            if (ws && (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.CLOSING)) {
                const checkInterval = setInterval(() => {
                    if (ws.readyState === WebSocket.OPEN) {
                        clearInterval(checkInterval);
                        resolve(ws);
                    } else if (ws.readyState === WebSocket.CLOSED) {
                        clearInterval(checkInterval);
                        reject(new Error("WebSocket connection closed"));
                    }
                }, 100);
                return;
            }

            if (connectionAttempts >= MAX_CONNECTION_ATTEMPTS) {
                console.warn(`Maximum WebSocket connection attempts (${MAX_CONNECTION_ATTEMPTS}) reached. Not attempting to reconnect.`);
                return reject(new Error("Maximum connection attempts reached"));
            }

            isConnecting = true;
            connectionAttempts++;

            try {
                if (ws) {
                    try {
                        ws.close();
                    } catch (e) {
                        console.error("Error closing existing WebSocket:", e);
                    }
                }

                const websocketUrl = process.env.REACT_APP_WEBSOCKET_URL;

                if (!websocketUrl) {
                    console.error("WebSocket URL is not defined in environment variables");
                    isConnecting = false;
                    reject(new Error("WebSocket URL is not defined"));
                    return;
                }

                console.log("Attempting to connect to WebSocket URL:", websocketUrl);

                try {
                    ws = new WebSocket(websocketUrl);
                } catch (error) {
                    console.error(`Invalid WebSocket URL (${websocketUrl}):`, error);
                    isConnecting = false;
                    reject(error);
                    return;
                }

                ws.onopen = () => {
                    console.log(`WebSocket connection established successfully to ${websocketUrl}`);
                    resetConnectionAttempts();
                    isConnecting = false;
                    startIdleTimer();
                    try {
                        if (ws && ws.readyState === WebSocket.OPEN) {
                            ws.send(JSON.stringify({
                                isModalOpen: isModalOpen,
                                transcriptionService: window.__VOICE_SEARCH_ENV_SETTINGS__.TRANSCRIPTION_SERVICE,
                                isBrowserSpeech:isBrowserSpeech
                            }));
                            previousModalState = isModalOpen;
                        }
                    } catch (e) {
                        console.error("Failed to send initial modal state on WebSocket open:", e);
                    }
                    resolve(ws);
                };

                ws.onerror = (error) => {
                    console.error(`WebSocket error with URL ${websocketUrl}:`, error);
                    isConnecting = false;
                    try {
                        if (lastHoldValue === true && typeof onMessageCallback === 'function') {
                            onMessageCallback({text: '', hold: false});
                            lastHoldValue = false;
                        }
                    } catch (e) {
                        console.error('Error handling WebSocket onerror cleanup:', e);
                    }
                    reject(error);
                };

                ws.onclose = (event) => {
                    console.log(`WebSocket connection closed. Code: ${event.code}, Reason: ${event.reason || 'No reason provided'}`);
                    try {
                        clearIdleTimer();
                        isConnecting = false;
                        if (lastHoldValue === true && typeof onMessageCallback === 'function') {
                            onMessageCallback({text: '', hold: false});
                            lastHoldValue = false;
                        }
                    } catch (e) {
                        console.error('Error handling WebSocket onclose cleanup:', e);
                    }
                };

                ws.onmessage = (msg) => {
                    try {
                        const data = JSON.parse(msg.data);
                        let text = '';
                        let hold = null;

                        if (data.type === "transcription" && data.text) {
                            text = data.text;
                        } else if (data.text) {
                            text = data.text;
                        } else if (data.transcription) {
                            text = data.transcription;
                        }

                        if (data.hold !== undefined) {
                            hold = data.hold;
                        }

                        if (hold !== null) {
                            lastHoldValue = hold;
                        }

                        onMessageCallback({text, hold});
                    } catch (error) {
                        console.error("Error parsing WebSocket message:", error);
                    }
                };
            } catch (error) {
                console.error("Error setting up WebSocket:", error);
                isConnecting = false;
                reject(error);
            }
        });
    };

    const setupWebSocket = (isActiveTab, isRecordingRef) => {
        if (ws && ws.readyState === WebSocket.OPEN) {
            return ws;
        }
        return ws;
    };

    const closeWebSocket = () => {
        clearIdleTimer();

        if (ws) {
            try {
                ws.close();
            } catch (e) {
                console.error("Error closing WebSocket:", e);
            }
            ws = null;
        }
        isConnecting = false;
    };

    const setModalState = (isOpen) => {
        previousModalState = isModalOpen;
        isModalOpen = isOpen;
    };

    const sendData = async (data) => {
        if (!ws || ws.readyState !== WebSocket.OPEN) {
            try {
                await setupWebSocketAsync();
            } catch (error) {
                console.error("Failed to establish WebSocket connection:", error);
                return;
            }
        }
        if (ws && ws.readyState === WebSocket.OPEN) {
            clearIdleTimer();
            if (data instanceof ArrayBuffer) {
                if (isModalOpen !== previousModalState) {
                    previousModalState = isModalOpen;

                    ws.send(JSON.stringify({
                        isModalOpen: isModalOpen,
                        TRANSCRIPTION_SERVICE: window.__VOICE_SEARCH_ENV_SETTINGS__.TRANSCRIPTION_SERVICE,
                        isBrowserSpeech:isBrowserSpeech
                    }));
                }
                ws.send(data);
            } else {
                ws.send(data);
            }
        } else {
            console.warn("Cannot send data: WebSocket is not open");
        }
    };

    return {
        setupWebSocket,
        setupWebSocketAsync,
        closeWebSocket,
        sendData,
        updateCallback,
        setModalState,
        getWebSocket: () => ws,
        isConnected: () => ws && ws.readyState === WebSocket.OPEN
    };
};

export default createWebSocketService;
